import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button } from '@pantry/ui';
import { LanguageIcon } from '@heroicons/react/24/outline';
import { TranslationContributionDialog } from './TranslationContributionDialog';
import { useProductTranslationStatus } from '../../hooks/useProductLanguage';
import type { Product } from '@pantry/api-client';
import { SUPPORTED_LANGUAGES } from '@pantry/shared';

interface TranslationIndicatorProps {
  product: Product;
  currentName: string;
  /** Whether to show as a compact inline indicator or a larger block */
  variant?: 'inline' | 'block';
}

/**
 * Shows when a product is using a fallback language and offers
 * the user the option to contribute a translation.
 */
export function TranslationIndicator({
  product,
  currentName,
  variant = 'inline',
}: TranslationIndicatorProps) {
  const { t } = useLingui();
  const [dialogOpen, setDialogOpen] = useState(false);
  const translationStatus = useProductTranslationStatus(product);

  // Don't show if product already has the preferred translation or can't contribute
  if (translationStatus.hasPreferredTranslation || !translationStatus.canContribute) {
    return null;
  }

  const preferredLanguageName =
    SUPPORTED_LANGUAGES.find((l) => l.code === translationStatus.preferredLanguage)?.name ||
    translationStatus.preferredLanguage;

  const fallbackLanguageName = translationStatus.fallbackLanguage
    ? SUPPORTED_LANGUAGES.find((l) => l.code === translationStatus.fallbackLanguage)?.name ||
      translationStatus.fallbackLanguage
    : 'default';

  if (variant === 'inline') {
    return (
      <>
        <button
          onClick={() => setDialogOpen(true)}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
          title={t`Add ${preferredLanguageName} translation`}
        >
          <LanguageIcon className="h-3.5 w-3.5" />
          <span>
            <Trans>Add translation</Trans>
          </span>
        </button>
        <TranslationContributionDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          product={product}
          preferredLanguage={translationStatus.preferredLanguage}
          currentName={currentName}
        />
      </>
    );
  }

  // Block variant - more prominent display
  return (
    <>
      <div className="rounded-xl bg-primary/5 border border-primary/10 p-3 space-y-2">
        <div className="flex items-start gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0 mt-0.5">
            <LanguageIcon className="h-3.5 w-3.5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-foreground">
              <Trans>This product name is in {fallbackLanguageName}</Trans>
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              <Trans>Help improve the app by adding a {preferredLanguageName} translation</Trans>
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDialogOpen(true)}
          className="w-full rounded-xl"
        >
          <LanguageIcon className="h-4 w-4 mr-2" />
          <Trans>Add Translation</Trans>
        </Button>
      </div>
      <TranslationContributionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        product={product}
        preferredLanguage={translationStatus.preferredLanguage}
        currentName={currentName}
      />
    </>
  );
}
